import { useGetOneFormSchema } from "#src/components/hooks/adapters/useFormSchema";
import { useEffect, useMemo, useState } from "react";
import {
  formSchemaSectionIdMatchesPathSegment,
  formSchemaIdMatchesWorkflowPathSegment,
} from "../Forms/WorkflowConfigurationOutputVariableForm.helpers";

/**
 * Faulty key (either "output" or "input") indicating where the non-matching question is, output or input.
 */
type FaultyKey = "input" | "output";

/**
 * This hook checks to see if the newly selected form schema is "compatible" with the existing form schema
 * when editing a "Submit Form" user task in workflow/workflow template configuration.
 * "compatible" means that existing output and input keys, should match paths in the new template.
 *
 * For example:
 *
 * output = {"$MYVAR": "$.answers.sectionId[sectionIndex].questionId.value"}
 * input = {"$.questions.questionId.default_answer": "$ANOTHERVAR"}
 *
 * What this hook does:
 * 1. When a new form schema is provided, it will fetch that schema (if it's different from the old form schema)
 * 2. For input, it iterates over all keys that look like: "$.questions.questionId.default_answer". "questionId" must be present in the newly selected form schema questions.
 * 3. For output, it iterates over all values that look like: "$.answers.sectionId[sectionIndex].questionId.value". sectionId should exist, and that sectionId should have questionId in it.
 * 4. All the missing questions are listed and returned from this hook.
 *
 * @returns an object with the following keys:
 * - results (array of questions not matching)
 * - isVerifying (boolean): Some API calls should be made to verify the data. When true, it means it's still checking.
 */
export const useVerifyNewFormSchema = ({
  output,
  input,
  currentFormSchemaId,
  selectedFormSchemaId,
}: {
  output?: Record<string, string>;
  input?: Record<string, string>;
  currentFormSchemaId?: string;
  selectedFormSchemaId?: string;
}) => {
  const valuesToCheck = useMemo(() => {
    const outputValues = Object.values(output ?? {}).map((v) => {
      const [sectionIdAndIndex, questionId] = v
        .replace("$.form_trigger.answers.", "")
        .replace("$.answers.", "")
        .replace(".value", "")
        .split(".");
      const sectionId = sectionIdAndIndex.split("[")[0];
      return { sectionId, questionId };
    });

    const inputValues = Object.keys(input ?? {}).map((v) => ({
      sectionId: "*",
      questionId: v.replace("$.questions.", "").replace(".default_answer", ""),
    }));

    return [...inputValues, ...outputValues];
  }, [input, output]);

  const isFetchOldTemplateEnabled =
    !!currentFormSchemaId && valuesToCheck.length > 0;

  const isFetchNewTemplateEnabled = !!(
    currentFormSchemaId &&
    selectedFormSchemaId &&
    currentFormSchemaId !== selectedFormSchemaId &&
    valuesToCheck.length > 0
  );

  const currentFormSchemaQuery = useGetOneFormSchema(
    { id: currentFormSchemaId ?? "" },
    {
      enabled: isFetchOldTemplateEnabled,
    }
  );

  const selectedFormSchemaQuery = useGetOneFormSchema(
    { id: selectedFormSchemaId ?? "" },
    {
      enabled: isFetchNewTemplateEnabled,
    }
  );

  const selectedFormSchema = selectedFormSchemaQuery.data?.data;

  const currentFormSchema = currentFormSchemaQuery.data?.data;

  const [results, setResults] = useState<
    Array<{
      faultyKey: FaultyKey;
      sectionName?: string;
      questionName: string;
    }>
  >([]);

  useEffect(() => {
    if (isFetchNewTemplateEnabled && selectedFormSchema) {
      const verificationResults = valuesToCheck
        .map((output) => {
          if (output.sectionId === "*") {
            const allQuestionIds = Object.keys(
              selectedFormSchema.config.questions
            );

            if (!allQuestionIds.includes(output.questionId)) {
              const oldQuestion =
                currentFormSchema?.config.questions[output.questionId];
              return {
                questionName: oldQuestion?.prompt ?? output.questionId,
                faultyKey: "input" as FaultyKey,
              };
            }
          }

          const section = selectedFormSchema.config.sections.find((s) =>
            formSchemaSectionIdMatchesPathSegment(s.id, output.sectionId)
          );

          if (
            !section ||
            !section.questions.some((questionId) =>
              formSchemaIdMatchesWorkflowPathSegment(
                questionId,
                output.questionId
              )
            )
          ) {
            const oldSection = currentFormSchema?.config.sections.find((s) =>
              formSchemaSectionIdMatchesPathSegment(s.id, output.sectionId)
            );
            const oldQuestionEntry = Object.entries(
              currentFormSchema?.config.questions ?? {}
            ).find(([questionId]) =>
              formSchemaIdMatchesWorkflowPathSegment(
                questionId,
                output.questionId
              )
            );
            const oldQuestion = oldQuestionEntry?.[1];

            return {
              sectionName: oldSection?.name ?? output.sectionId,
              questionName: oldQuestion?.prompt ?? output.questionId,
              faultyKey: "output" as FaultyKey,
            };
          }

          return false;
        })
        .filter((e) => !!e);
      setResults(verificationResults);
    } else {
      setResults([]);
    }
  }, [
    isFetchNewTemplateEnabled,
    selectedFormSchema,
    valuesToCheck,
    currentFormSchema,
    setResults,
  ]);

  return {
    results,
    isVerifying:
      (isFetchNewTemplateEnabled && selectedFormSchemaQuery.isLoading) ||
      (isFetchOldTemplateEnabled && currentFormSchemaQuery.isLoading),
  };
};
